import React from 'react';

import AudienceSolutionPage, { AudiencePageData } from 'components/common/AudienceSolutionPage';

const data: AudiencePageData = {
  eyebrow: 'Storycards for enterprises',
  title: 'One platform for every team creating digital experiences.',
  subtitle:
    'Give marketing, product, digital, data and HR teams the freedom to create, while your organization maintains control of brand, security, permissions and data.',
  heroPanelTitle: 'Create freely. Operate centrally.',
  heroPanelItems: ['Professional creation for every team', 'Central brand and product governance', 'First-party data in your own stack'],
  audienceLabel: 'Built for complex organizations',
  audienceItems: ['Banks', 'Insurance', 'Real estate', 'Consumer brands', 'Technology companies'],
  industrySolutions: {
    title: 'Built for real enterprise challenges.',
    intro:
      'Turn complex decisions, content and customer journeys into useful interactive products that create value for the audience and structured data for the organization.',
    items: [
      {
        industry: 'Banking & lending',
        title: 'Guide customers through high-consideration financial decisions.',
        challenge:
          'Customers rarely arrive knowing which mortgage, loan or financial product fits their situation. Replace static explanations and early lead forms with a guided journey that helps them understand their options.',
        products: ['Mortgage advisor', 'Product matcher', 'Eligibility flow', 'Financial health assessment'],
        data:
          'Customer needs, intent, life stage, preferences, selected pathways and completion behavior.',
        value:
          'Recommend a relevant next step, qualify demand and enrich your CRM or customer data platform with useful context.',
      },
      {
        industry: 'Insurance',
        title: 'Make coverage easier to understand and act on.',
        challenge:
          'Policies and coverage choices are difficult to compare. Create a clear, personalized experience that translates complexity into questions customers can confidently answer.',
        products: ['Coverage finder', 'Needs assessment', 'Renewal journey', 'Claims preparation flow'],
        data:
          'Coverage priorities, household or business context, declared needs, intent and journey drop-offs.',
        value:
          'Help customers reach the right coverage or service path and give sales and service teams better information for follow-up.',
      },
      {
        industry: 'Real estate',
        title: 'Turn property discovery into a personalized matching journey.',
        challenge:
          'A gallery and a generic contact form reveal very little about what a buyer actually needs. Help people explore projects and properties through a useful, visual decision experience.',
        products: ['Property matcher', 'Interactive project site', 'Apartment comparison', 'Lead qualification journey'],
        data:
          'Budget range, preferred location, property type, room requirements, purchase timeline and viewing intent.',
        value:
          'Match prospects with relevant inventory and route richer, better-contextualized leads to the right project or agent.',
      },
      {
        industry: 'Consumer brands',
        title: 'Help every customer discover the right product and story.',
        challenge:
          'Large catalogs and one-size-fits-all pages make discovery harder. Build branded experiences that adapt content, recommendations and calls to action to each audience.',
        products: ['Product finder', 'Launch minisite', 'Interactive magazine', 'Brand education quiz'],
        data:
          'Preferences, product affinity, content interests, declared needs, interaction patterns and purchase intent.',
        value:
          'Create more relevant journeys, connect engagement to audience segments and continuously improve the experience.',
      },
    ],
  },
  useCasesTitle: 'One platform. Different teams. Real products.',
  useCasesIntro:
    'Storycards gives every department the tools to solve its own business challenge without creating another disconnected system.',
  useCases: [
    {
      title: 'Marketing & growth',
      text: 'Launch branded experiences that help audiences discover products, share intent and move toward the right next action.',
      examples: ['Product advisors', 'Lead flows', 'Personalized minisites', 'Quizzes'],
    },
    {
      title: 'Digital & product',
      text: 'Add guided, interactive experiences to your existing websites and apps without waiting for a full product release cycle.',
      examples: ['Onboarding', 'Calculators', 'Product education', 'Embedded widgets'],
    },
    {
      title: 'HR & internal communications',
      text: 'Turn employee information into clear, personalized journeys that are easier to understand and complete.',
      examples: ['Employee onboarding', 'Benefits advisors', 'Training', 'Internal surveys'],
    },
    {
      title: 'Data, CRM & insights',
      text: 'Collect structured first-party data through useful experiences and send it directly into the systems your teams already use.',
      examples: ['Segmentation', 'Data enrichment', 'Drop-off analysis', 'Real-time events'],
    },
  ],
  workflowTitle: 'From idea to an operating product.',
  workflowIntro:
    'Move quickly without losing the controls large organizations need. Every stage stays connected inside one governed platform.',
  workflowSteps: [
    { title: 'Create', text: 'Start with AI, Figma, an approved template or a blank canvas.' },
    { title: 'Perfect', text: 'Use the professional studio to control every layout, breakpoint and interaction.' },
    { title: 'Govern', text: 'Apply roles, permissions, approvals and brand rules before anything goes live.' },
    { title: 'Connect', text: 'Embed anywhere and send every relevant event to your data stack in real time.' },
    { title: 'Improve', text: 'Measure behavior, identify drop-offs and optimize performance continuously.' },
  ],
  foundationTitle: 'The enterprise layer behind every experience.',
  foundationIntro:
    'Teams get creative freedom. The organization gets one place to manage products, people, brands and data at scale.',
  foundation: [
    {
      title: 'Central product and template library',
      text: 'Reuse approved products, templates and components across departments while preserving consistent quality.',
    },
    {
      title: 'Roles, approvals and SSO',
      text: 'Control who can create, review, publish, edit content or access data across teams and product portfolios.',
    },
    {
      title: 'Brand-safe creation',
      text: 'Designers define the system and protected editing lets content teams move quickly without breaking it.',
    },
    {
      title: 'Data ownership and security',
      text: 'Transfer first-party data to your own stack in real time, with enterprise-grade security and compliance controls.',
    },
    {
      title: 'Flexible deployment at scale',
      text: 'Publish standalone experiences or embed them into existing websites and apps on infrastructure built for high traffic.',
    },
  ],
  examplesTitle: '',
  examplesIntro: '',
  examples: [],
  ctaTitle: 'Give every team the power to create. Keep the organization in control.',
  ctaText: 'Bring creation, governance, data and optimization into one enterprise platform.',
};

const Enterprise: React.FC = () => <AudienceSolutionPage data={data} />;

export default Enterprise;
